import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";

// Local Components
import { addEmail, addPhone } from "features/user/userSlice";
import { verifySignedUrl } from "utils/api";
import {
  appendWarrantyClaimParam,
  getWarrantyClaimUrlParam,
} from "utils/warrantyClaim";
import { getReturnFlowDeepLink } from "utils/returnFlowDeepLink";

function Presigned({ loadConfigurations }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasStartedVerificationRef = useRef(false);

  const encipherencode = params.get("encipherencode");
  const staff_email = params.get("staff_email");
  const p = params.get("p");
  const e = params.get("e");
  const signed_key = params.get("signed_key");
  const oid = params.get("oid");
  const returnFlowDeepLink = useMemo(
    () => getReturnFlowDeepLink(params),
    [params],
  );
  const isInIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch (err) {
      return true;
    }
  }, []);
  const accessToken = useMemo(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    return hashParams.get("access_token");
  }, []);
  const rawWarrantyClaimParam = useMemo(() => {
    const rawParams = new URLSearchParams(window.location.search);
    return getWarrantyClaimUrlParam(rawParams);
  }, []);
  const explicitWarrantyClaimParam =
    getWarrantyClaimUrlParam(params) ?? rawWarrantyClaimParam;
  const hasExplicitDestination = Boolean(oid) ||
    Boolean(returnFlowDeepLink) ||
    ["true", "false"].includes(explicitWarrantyClaimParam?.toLowerCase());
  const targetOrdersPath = useMemo(() => {
    const targetParams = appendWarrantyClaimParam(params);
    const warrantyClaimParam = explicitWarrantyClaimParam?.toLowerCase();
    if (["true", "false"].includes(warrantyClaimParam)) {
      targetParams.set("claim_warrenty", warrantyClaimParam);
    }

    if (encipherencode) {
      targetParams.set("encipherencode", encipherencode);
    }

    if (oid) {
      targetParams.set("oid", oid);
    }

    if (returnFlowDeepLink) {
      targetParams.set("line_item_id", returnFlowDeepLink.lineItemId);
      targetParams.set("return_type", returnFlowDeepLink.returnType);
    }

    const query = targetParams.toString();
    const targetPath = oid && returnFlowDeepLink
      ? "/v2/products"
      : "/v2/orders";
    return `${targetPath}${query ? `?${query}` : ""}`;
  }, [
    encipherencode,
    oid,
    params,
    explicitWarrantyClaimParam,
    returnFlowDeepLink,
  ]);
  const verificationSessionKey = useMemo(() => {
    if (accessToken) {
      return `presigned-token:${encipherencode}:${p || ""}:${e || ""}:${accessToken}`;
    }

    if (!encipherencode || !staff_email || !signed_key) {
      return null;
    }

    return `presigned-verification:${encipherencode}:${staff_email}:${p || ""}:${e || ""}:${signed_key}`;
  }, [accessToken, e, encipherencode, p, signed_key, staff_email]);

  useEffect(() => {
    if (!loadConfigurations) return;
    if (!verificationSessionKey) return;
    if (hasStartedVerificationRef.current) return;

    const verificationStatus = sessionStorage.getItem(verificationSessionKey);

    if (verificationStatus === "pending" || verificationStatus === "done") {
      return;
    }

    if (p) {
      dispatch(addPhone(p));
    }

    if (e) {
      dispatch(addEmail(e));
    }

    hasStartedVerificationRef.current = true;
    sessionStorage.setItem(verificationSessionKey, "pending");

    if (accessToken) {
      sessionStorage.setItem(verificationSessionKey, "done");
      sessionStorage.setItem("access_token", accessToken);
      navigate(targetOrdersPath, { replace: true });
      return;
    }

    verifySignedUrl(encipherencode, staff_email, p, e, signed_key)
      .then((resp) => {
        sessionStorage.setItem(verificationSessionKey, "done");
        sessionStorage.setItem("access_token", resp.data.access_token);
        const savedRoute =
          isInIframe && localStorage.getItem("rms_last_route_source") === "iframe"
            ? localStorage.getItem("rms_last_route")
            : null;
        if (hasExplicitDestination) {
          navigate(targetOrdersPath);
        } else if (
          savedRoute &&
          savedRoute.startsWith("/v2/") &&
          savedRoute !== "/v2/returns" &&
          savedRoute !== "/v2/presignedurl" &&
          savedRoute !== "/v2/orders"
        ) {
          localStorage.removeItem("rms_last_route");
          localStorage.removeItem("rms_last_route_source");
          navigate("/v2/orders", { replace: true });
          navigate(savedRoute);
        } else {
          navigate(targetOrdersPath);
        }
      })
      .catch((err) => {
        console.log(err);
        sessionStorage.removeItem(verificationSessionKey);
        sessionStorage.setItem("return_page", "presigned");
        navigate("/error");
      });
  }, [
    dispatch,
    e,
    encipherencode,
    accessToken,
    isInIframe,
    loadConfigurations,
    navigate,
    p,
    signed_key,
    staff_email,
    targetOrdersPath,
    hasExplicitDestination,
    verificationSessionKey,
  ]);

  return (
    <div>
      <div
        style={{
          height: "100vh",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "-30px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <ClipLoader
            color="#3B444D"
            size={50}
            speedMultiplier={0.75}
            cssOverride={{ borderWidth: "6px" }}
          />
          <p style={{ fontSize: "14px", color: "#3B444D", marginTop: "10px" }}>
            Authenticating...
          </p>
        </div>
      </div>
    </div>
  );
}

export default Presigned;
